import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  Briefcase,
  CalendarClock,
  Download,
  Edit3,
  Eye,
  FileText,
  Gavel,
  Loader,
  Mail,
  Plus,
  RefreshCw,
  Scale,
  Send,
  Settings,
  Upload,
  X,
} from 'lucide-react';
import { GlassButton, GlassCard, GlassInput, FadeIn } from '../../components/ui';
import { downloadDocument, previewDocument } from '../../utils/documentViewer';

const API = '/erp/api/v1';
const today = new Date().toISOString().split('T')[0];

async function getToken() {
  const token = localStorage.getItem('nexus_token');
  if (!token) {
    window.location.assign('/erp/login');
    return '';
  }
  return token;
}

async function authHeaders() {
  const token = await getToken();
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

async function authOnlyHeaders() {
  const token = await getToken();
  return { Authorization: `Bearer ${token}` };
}

const emptyProcesso = {
  titulo: '',
  numero: '',
  tipo: 'processo',
  area: 'civel',
  status: 'ativo',
  fase: '',
  prioridade: 'media',
  cliente_nome: '',
  parte_autora: '',
  parte_re: '',
  advogado_responsavel: '',
  comarca: '',
  vara: '',
  modulo_origem: 'construtora',
  unidade_id: '',
  contrato_id: '',
  obra_id: '',
  valor_causa: '',
  provisao: '',
  probabilidade_perda: 'possivel',
  data_distribuicao: '',
  proximo_prazo: '',
  proxima_acao: '',
  resumo: '',
  observacao: '',
};

const emptyCobranca = {
  cliente_nome: '',
  documento_cliente: '',
  email: '',
  telefone: '',
  unidade: '',
  contrato: '',
  tipo: 'extrajudicial',
  status: 'aberta',
  prioridade: 'media',
  modulo_origem: 'financeiro',
  valor_original: '',
  valor_atualizado: '',
  vencimento_original: '',
  data_envio: today,
  canal: 'email',
  responsavel: '',
  proxima_acao: '',
  proximo_prazo: '',
  observacao: '',
};

const emptyPrazo = {
  processo_id: '',
  cobranca_id: '',
  tipo: 'prazo',
  titulo: '',
  data_prazo: today,
  hora: '',
  responsavel: '',
  observacao: '',
};

const emptyDoc = {
  processo_id: '',
  cobranca_id: '',
  tipo: 'documento',
  data_documento: today,
  observacao: '',
  file: null,
};

const emptyImportPdf = {
  destino: 'processo',
  processo_id: '',
  cobranca_id: '',
  modulo_origem: 'juridico',
  prioridade: 'media',
  observacao: '',
  file: null,
};

const emptyConfig = {
  escritorio_nome: '',
  escritorio_email: '',
  smtp_server: 'smtp.gmail.com',
  smtp_port: 587,
  smtp_use_tls: true,
  smtp_username: '',
  smtp_password: '',
  smtp_auth_mode: 'password',
  oauth_client_id: '',
  oauth_client_secret: '',
  oauth_refresh_token: '',
  mail_default_sender: '',
  resposta_automatica: true,
  observacao: '',
};

const statusColors = {
  ativo: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/20',
  risco: 'text-red-300 bg-red-500/10 border-red-500/20',
  encerrado: 'text-white/50 bg-white/5 border-white/10',
  aberta: 'text-amber-300 bg-amber-500/10 border-amber-500/20',
  enviada: 'text-sky-300 bg-sky-500/10 border-sky-500/20',
  negociacao: 'text-violet-300 bg-violet-500/10 border-violet-500/20',
  judicializada: 'text-red-300 bg-red-500/10 border-red-500/20',
  pendente: 'text-amber-300 bg-amber-500/10 border-amber-500/20',
  cumprido: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/20',
  enviado: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/20',
  fila: 'text-amber-300 bg-amber-500/10 border-amber-500/20',
  erro: 'text-red-300 bg-red-500/10 border-red-500/20',
  rascunho: 'text-white/50 bg-white/5 border-white/10',
};

const fmtBRL = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
const fmtDate = (s) => s ? new Date(`${s}T12:00:00`).toLocaleDateString('pt-BR') : '-';

function StatusBadge({ value }) {
  return (
    <span className={`inline-flex rounded-full border px-2 py-1 text-xs ${statusColors[value] || 'text-white/60 bg-white/5 border-white/10'}`}>
      {value || '-'}
    </span>
  );
}

function Field({ label, children }) {
  return (
    <label className="space-y-1">
      <span className="text-xs text-white/50">{label}</span>
      {children}
    </label>
  );
}

const openDocumentosOCR = (tipo = 'juridico_processo') => {
  const params = new URLSearchParams({ acao: 'importar', modulo: 'juridico', tipo });
  window.location.assign(`/erp/documentos?${params.toString()}`);
};

export const Juridico = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('processos');
  const [dashboard, setDashboard] = useState(null);
  const [processos, setProcessos] = useState([]);
  const [cobrancas, setCobrancas] = useState([]);
  const [prazos, setPrazos] = useState([]);
  const [corpusDocs, setCorpusDocs] = useState([]);
  const [emails, setEmails] = useState([]);
  const [juridicoConfig, setJuridicoConfig] = useState(emptyConfig);
  const [selectedProcessoId, setSelectedProcessoId] = useState('');
  const [dossie, setDossie] = useState(null);
  const [modal, setModal] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [processoForm, setProcessoForm] = useState(emptyProcesso);
  const [cobrancaForm, setCobrancaForm] = useState(emptyCobranca);
  const [prazoForm, setPrazoForm] = useState(emptyPrazo);
  const [docForm, setDocForm] = useState(emptyDoc);
  const [importPdfForm, setImportPdfForm] = useState(emptyImportPdf);
  const [configForm, setConfigForm] = useState(emptyConfig);

  const selectedProcesso = useMemo(
    () => processos.find((item) => item.id === selectedProcessoId),
    [processos, selectedProcessoId]
  );

  const loadDossie = useCallback(async (id = selectedProcessoId) => {
    if (!id) {
      setDossie(null);
      return;
    }
    const headers = await authHeaders();
    const res = await fetch(`${API}/juridico/processos/${id}/dossie`, { headers });
    if (res.ok) setDossie(await res.json());
  }, [selectedProcessoId]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const headers = await authHeaders();
      const [dashRes, procRes, cobRes, prazoRes, corpusRes, emailsRes, configRes] = await Promise.all([
        fetch(`${API}/juridico/dashboard`, { headers }),
        fetch(`${API}/juridico/processos`, { headers }),
        fetch(`${API}/juridico/cobrancas`, { headers }),
        fetch(`${API}/juridico/prazos`, { headers }),
        fetch(`${API}/juridico/corpus/relatorio`, { headers }),
        fetch(`${API}/juridico/emails`, { headers }),
        fetch(`${API}/juridico/config`, { headers }),
      ]);
      if (dashRes.ok) setDashboard(await dashRes.json());
      if (procRes.ok) {
        const data = await procRes.json();
        const items = data.items || [];
        setProcessos(items);
        if (!selectedProcessoId && items.length) setSelectedProcessoId(items[0].id);
      }
      if (cobRes.ok) setCobrancas((await cobRes.json()).items || []);
      if (prazoRes.ok) setPrazos((await prazoRes.json()).items || []);
      if (corpusRes.ok) setCorpusDocs((await corpusRes.json()).items || []);
      if (emailsRes.ok) setEmails((await emailsRes.json()).items || []);
      if (configRes.ok) {
        const cfg = await configRes.json();
        setJuridicoConfig(cfg);
        setConfigForm({ ...emptyConfig, ...cfg, smtp_password: '' });
      }
    } catch (e) {
      setError('Erro ao carregar Jurídico');
    } finally {
      setLoading(false);
    }
  }, [selectedProcessoId]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadDossie(); }, [loadDossie]);

  useEffect(() => {
    const action = searchParams.get('acao');
    if (!action) return;
    const prefill = {
      cliente_nome: searchParams.get('cliente') || '',
      valor_original: searchParams.get('valor') || '',
      valor_atualizado: searchParams.get('valor') || '',
      vencimento_original: searchParams.get('vencimento') || '',
      observacao: searchParams.get('observacao') || '',
    };
    const handlers = {
      importar_pdf: () => { setImportPdfForm(emptyImportPdf); setModal('importPdf'); },
      cobranca: () => { setActiveTab('cobrancas'); setCobrancaForm({ ...emptyCobranca, ...prefill }); setModal('cobranca'); },
      processo: () => { setActiveTab('processos'); setProcessoForm(emptyProcesso); setModal('processo'); },
      prazo: () => { setActiveTab('prazos'); setPrazoForm(emptyPrazo); setModal('prazo'); },
      config: () => setActiveTab('config'),
      documento: () => { setDocForm(emptyDoc); setModal('documento'); },
    };
    if (handlers[action]) handlers[action]();
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const closeModal = () => {
    setModal('');
    setError('');
    setNotice('');
  };

  const afterSave = async () => {
    closeModal();
    await loadData();
    await loadDossie();
  };

  const saveJson = async (path, body, success) => {
    setSaving(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}${path}`, { method: body.id ? 'PUT' : 'POST', headers, body: JSON.stringify(body) });
      if (!res.ok) throw new Error();
      const saved = await res.json();
      if (success) success(saved);
      await afterSave();
    } catch (e) {
      setError('Erro ao salvar registro');
    } finally {
      setSaving(false);
    }
  };

  const saveProcesso = () => {
    if (!processoForm.titulo) {
      setError('Título é obrigatório');
      return;
    }
    const path = processoForm.id ? `/juridico/processos/${processoForm.id}` : '/juridico/processos';
    saveJson(path, {
      ...processoForm,
      valor_causa: processoForm.valor_causa ? Number(processoForm.valor_causa) : 0,
      provisao: processoForm.provisao ? Number(processoForm.provisao) : 0,
    }, (saved) => setSelectedProcessoId(saved.id));
  };

  const saveCobranca = () => {
    if (!cobrancaForm.cliente_nome) {
      setError('Cliente é obrigatório');
      return;
    }
    const path = cobrancaForm.id ? `/juridico/cobrancas/${cobrancaForm.id}` : '/juridico/cobrancas';
    saveJson(path, {
      ...cobrancaForm,
      valor_original: cobrancaForm.valor_original ? Number(cobrancaForm.valor_original) : 0,
      valor_atualizado: cobrancaForm.valor_atualizado ? Number(cobrancaForm.valor_atualizado) : 0,
    });
  };

  const openProcessoEdit = (item) => {
    setProcessoForm({ ...emptyProcesso, ...item });
    setModal('processo');
  };

  const openCobrancaEdit = (item) => {
    setCobrancaForm({ ...emptyCobranca, ...item });
    setModal('cobranca');
  };

  const savePrazo = () => {
    if (!prazoForm.titulo || !prazoForm.data_prazo) {
      setError('Título e data são obrigatórios');
      return;
    }
    saveJson('/juridico/prazos', prazoForm);
  };

  const uploadDocumento = async () => {
    if (!docForm.file) {
      setError('Arquivo é obrigatório');
      return;
    }
    setSaving(true);
    try {
      const headers = await authOnlyHeaders();
      const form = new FormData();
      Object.entries(docForm).forEach(([key, value]) => {
        if (value) form.append(key, value);
      });
      const res = await fetch(`${API}/juridico/documentos`, { method: 'POST', headers, body: form });
      if (!res.ok) throw new Error();
      await afterSave();
    } catch (e) {
      setError('Erro ao anexar documento');
    } finally {
      setSaving(false);
    }
  };

  const importarPdf = async () => {
    if (!importPdfForm.file) {
      setError('Arquivo PDF é obrigatório');
      return;
    }
    setSaving(true);
    try {
      const headers = await authOnlyHeaders();
      const form = new FormData();
      Object.entries(importPdfForm).forEach(([key, value]) => {
        if (value) form.append(key, value);
      });
      const res = await fetch(`${API}/juridico/importar-pdf`, { method: 'POST', headers, body: form });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setModal('');
      await loadData();
      if (data.processo) {
        setSelectedProcessoId(data.processo.id);
        setProcessoForm({ ...emptyProcesso, ...data.processo });
        setModal('processo');
        setNotice('PDF importado. Revise os campos extraídos antes de seguir.');
      } else if (data.cobranca) {
        setCobrancaForm({ ...emptyCobranca, ...data.cobranca });
        setModal('cobranca');
        setNotice('PDF importado. Revise a cobrança extraída antes de encaminhar ao escritório.');
      } else {
        setNotice('PDF importado.');
      }
    } catch (e) {
      setError('Erro ao importar PDF jurídico');
    } finally {
      setSaving(false);
    }
  };

  const enviarEscritorio = async (cobrancaId) => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/juridico/cobrancas/${cobrancaId}/enviar-escritorio`, {
        method: 'POST',
        headers,
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      const status = data.email?.status === 'enviado' ? 'enviado' : 'salvo na fila';
      setNotice(`Briefing do escritório ${status}.`);
      await loadData();
    } catch (e) {
      setError('Erro ao enviar briefing ao escritório');
    } finally {
      setSaving(false);
    }
  };

  const reenviarEmail = async (emailId) => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/juridico/emails/${emailId}/reenviar`, {
        method: 'POST',
        headers,
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setNotice(data.status === 'enviado' ? 'E-mail reenviado ao escritório.' : 'E-mail permaneceu na fila; confira a configuração SMTP.');
      await loadData();
    } catch (e) {
      setError('Erro ao reenviar e-mail');
    } finally {
      setSaving(false);
    }
  };

  const salvarConfig = async () => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const headers = await authHeaders();
      const payload = { ...configForm };
      if (!payload.smtp_password) delete payload.smtp_password;
      const res = await fetch(`${API}/juridico/config`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      const cfg = await res.json();
      setJuridicoConfig(cfg);
      setConfigForm({ ...emptyConfig, ...cfg, smtp_password: '' });
      setNotice('Configuração jurídica salva.');
    } catch (e) {
      setError('Erro ao salvar configuração jurídica');
    } finally {
      setSaving(false);
    }
  };

  const testarEmail = async () => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/juridico/emails/testar`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ destinatario: configForm.escritorio_email }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setNotice(data.status === 'enviado' ? 'E-mail de teste enviado.' : 'Teste salvo na fila; confira SMTP e destinatário.');
      await loadData();
    } catch (e) {
      setError('Erro ao testar e-mail jurídico');
    } finally {
      setSaving(false);
    }
  };

  const juridicoDocUrl = (id) => `${API}/juridico/documentos/${id}/download`;
  const juridicoDocName = (id) => {
    const doc = (dossie?.documentos || []).find((item) => item.id === id);
    return doc?.nome_armazenado || doc?.nome_original || 'documento-juridico';
  };

  const previewDoc = async (id) => {
    const headers = await authOnlyHeaders();
    try {
      await previewDocument(juridicoDocUrl(id), juridicoDocName(id), headers);
    } catch (e) {
      setError(e.message || 'Erro ao abrir documento jurídico');
    }
  };

  const downloadDoc = async (id) => {
    const headers = await authOnlyHeaders();
    try {
      await downloadDocument(juridicoDocUrl(id), juridicoDocName(id), headers);
    } catch (e) {
      setError(e.message || 'Erro ao baixar documento jurídico');
    }
  };

  const tabs = [
    ['processos', 'Processos'],
    ['cobrancas', 'Cobranças'],
    ['prazos', 'Prazos'],
    ['corpus', 'Corpus Vívum'],
    ['dossie', 'Dossiê'],
    ['emails', 'E-mails'],
    ['config', 'Configuração'],
  ];

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center text-white/60">
        <Loader className="mr-2 h-5 w-5 animate-spin" /> Carregando Jurídico...
      </div>
    );
  }

  return (
    <FadeIn>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-white">Jurídico e Cobrança</h1>
            <p className="mt-1 text-sm text-white/50">Processos, cobranças, prazos, documentos e ações vinculadas ao ERP.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <a
              href={`${process.env.PUBLIC_URL || ''}/manuals/playbook-humano-juridico.md`}
              download
              className="relative inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/10 px-5 py-2.5 text-sm font-medium text-white/90 transition-all duration-300 ease-out hover:border-white/30 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-accent-primary/50"
            >
              <Download className="h-4 w-4" /> Manual
            </a>
            <GlassButton onClick={loadData}><RefreshCw className="h-4 w-4" /> Atualizar</GlassButton>
            <GlassButton onClick={() => openDocumentosOCR('juridico_processo')}><Upload className="h-4 w-4" /> Enviar documento</GlassButton>
            <GlassButton onClick={() => { setImportPdfForm(emptyImportPdf); setModal('importPdf'); }}><Upload className="h-4 w-4" /> Importar PDF</GlassButton>
            <GlassButton onClick={() => { setProcessoForm(emptyProcesso); setModal('processo'); }}><Plus className="h-4 w-4" /> Processo</GlassButton>
            <GlassButton onClick={() => { setCobrancaForm(emptyCobranca); setModal('cobranca'); }}><Plus className="h-4 w-4" /> Cobrança</GlassButton>
          </div>
        </div>

        {error && <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200">{error}</div>}
        {notice && <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-100">{notice}</div>}

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-6">
          {[
            ['Processos ativos', dashboard?.processos_ativos || 0, Briefcase],
            ['Cobranças abertas', dashboard?.cobrancas_abertas || 0, Scale],
            ['Prazos vencidos', dashboard?.prazos_vencidos || 0, AlertTriangle],
            ['Prazos 15 dias', dashboard?.prazos_15_dias || 0, CalendarClock],
            ['Provisão', fmtBRL(dashboard?.provisao_total), FileText],
            ['A cobrar', fmtBRL(dashboard?.valor_cobrancas_abertas), Gavel],
          ].map(([label, value, Icon]) => (
            <GlassCard key={label} className="p-4">
              <div className="flex items-center justify-between">
                <Icon className="h-5 w-5 text-white/50" />
              </div>
              <div className="mt-3 text-2xl font-semibold text-white">{value}</div>
              <div className="text-xs text-white/50">{label}</div>
            </GlassCard>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 border-b border-white/10 pb-2">
          {tabs.map(([key, label]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`rounded-lg px-3 py-2 text-sm ${activeTab === key ? 'bg-white/15 text-white' : 'text-white/50 hover:bg-white/5 hover:text-white'}`}
            >
              {label}
            </button>
          ))}
        </div>

        {activeTab === 'processos' && (
          <GlassCard className="overflow-hidden">
            <div className="grid grid-cols-12 border-b border-white/10 px-4 py-3 text-xs text-white/40">
              <div className="col-span-4">Processo</div>
              <div className="col-span-2">Cliente/Parte</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2">Prazo</div>
                <div className="col-span-2 text-right">Ação</div>
            </div>
            {processos.map((item) => (
              <button
                key={item.id}
                  onClick={() => { setSelectedProcessoId(item.id); setActiveTab('dossie'); }}
                  className="grid w-full grid-cols-12 border-b border-white/5 px-4 py-3 text-left text-sm hover:bg-white/5"
                >
                <div className="col-span-4">
                  <div className="font-medium text-white">{item.titulo}</div>
                  <div className="text-xs text-white/40">{item.numero || item.area}</div>
                </div>
                <div className="col-span-2 text-white/70">{item.cliente_nome || item.parte_re || '-'}</div>
                <div className="col-span-2"><StatusBadge value={item.status} /></div>
                <div className="col-span-2 text-white/70">{fmtDate(item.proximo_prazo)}</div>
                <div className="col-span-2 flex items-center justify-end gap-2 text-white/70">
                  <span>{fmtBRL(item.provisao)}</span>
                  <span
                    role="button"
                    tabIndex={0}
                    title="Editar processo"
                    onClick={(event) => { event.stopPropagation(); openProcessoEdit(item); }}
                    onKeyDown={(event) => { if (event.key === 'Enter') openProcessoEdit(item); }}
                    className="rounded-lg p-2 text-white/50 hover:bg-white/10 hover:text-white"
                  >
                    <Edit3 className="h-4 w-4" />
                  </span>
                </div>
              </button>
            ))}
          </GlassCard>
        )}

        {activeTab === 'cobrancas' && (
          <GlassCard className="overflow-hidden">
            <div className="grid grid-cols-12 border-b border-white/10 px-4 py-3 text-xs text-white/40">
              <div className="col-span-3">Cliente</div>
              <div className="col-span-2">Unidade/Contrato</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2">Próxima ação</div>
              <div className="col-span-1">Prazo</div>
              <div className="col-span-2 text-right">Valor</div>
            </div>
            {cobrancas.map((item) => (
              <div key={item.id} className="grid grid-cols-12 items-center border-b border-white/5 px-4 py-3 text-sm">
                <div className="col-span-3 text-white">{item.cliente_nome}</div>
                <div className="col-span-2 text-white/60">{item.unidade || item.contrato || '-'}</div>
                <div className="col-span-2"><StatusBadge value={item.status} /></div>
                <div className="col-span-2 text-white/60">{item.proxima_acao || '-'}</div>
                <div className="col-span-1 text-white/60">{fmtDate(item.proximo_prazo)}</div>
                <div className="col-span-2 flex items-center justify-end gap-2 text-white/70">
                  <span>{fmtBRL(item.valor_atualizado || item.valor_original)}</span>
                  <button
                    title="Editar cobrança"
                    onClick={() => openCobrancaEdit(item)}
                    className="rounded-lg p-2 text-white/50 hover:bg-white/10 hover:text-white"
                  >
                    <Edit3 className="h-4 w-4" />
                  </button>
                  <button
                    title="Enviar briefing ao escritório"
                    onClick={() => enviarEscritorio(item.id)}
                    className="rounded-lg p-2 text-white/50 hover:bg-white/10 hover:text-white"
                    disabled={saving}
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </GlassCard>
        )}

        {activeTab === 'prazos' && (
          <GlassCard className="p-4">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Agenda jurídica</h2>
              <GlassButton onClick={() => { setPrazoForm({ ...emptyPrazo, processo_id: selectedProcessoId }); setModal('prazo'); }}><Plus className="h-4 w-4" /> Prazo</GlassButton>
            </div>
            <div className="space-y-2">
              {prazos.map((item) => (
                <div key={item.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/5 p-3">
                  <div>
                    <div className="font-medium text-white">{item.titulo}</div>
                    <div className="text-xs text-white/50">{item.tipo} · {item.responsavel || 'sem responsável'}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-white/70">{fmtDate(item.data_prazo)} {item.hora || ''}</span>
                    <StatusBadge value={item.status} />
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>
        )}

        {activeTab === 'corpus' && (
          <div className="grid gap-4 xl:grid-cols-[1fr_360px]">
            <GlassCard className="overflow-hidden">
              <div className="grid grid-cols-12 border-b border-white/10 px-4 py-3 text-xs text-white/40">
                <div className="col-span-4">Documento vivo</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2">Tipo</div>
                <div className="col-span-2">Próximo prazo</div>
                <div className="col-span-2 text-right">Dias</div>
              </div>
              {corpusDocs.map((item) => (
                <div key={item.doc_id} className="grid grid-cols-12 border-b border-white/5 px-4 py-3 text-sm">
                  <div className="col-span-4">
                    <div className="font-medium text-white">{item.titulo}</div>
                    <div className="text-xs text-white/40">{item.doc_id}</div>
                  </div>
                  <div className="col-span-2"><StatusBadge value={item.status} /></div>
                  <div className="col-span-2 text-white/60">{item.tipo || '-'}</div>
                  <div className="col-span-2 text-white/70">
                    <div>{item.proximo_prazo || 'indefinido'}</div>
                    <div className="text-xs text-white/40">{fmtDate(item.data_prazo)}</div>
                  </div>
                  <div className={`col-span-2 text-right font-medium ${item.dias_restantes <= 7 ? 'text-red-200' : item.dias_restantes <= 30 ? 'text-amber-200' : 'text-white/70'}`}>
                    {item.dias_restantes === 999 ? '-' : item.dias_restantes}
                  </div>
                </div>
              ))}
              {!corpusDocs.length && <div className="p-6 text-sm text-white/50">Nenhum documento vivo cadastrado.</div>}
            </GlassCard>

            <GlassCard className="p-5">
              <h2 className="text-lg font-semibold text-white">Alertas do Corpus</h2>
              <div className="mt-4 space-y-3">
                {corpusDocs.flatMap((item) => (item.alertas_disparados || []).slice(-2).map((alerta, idx) => ({ ...alerta, titulo: item.titulo, id: `${item.doc_id}-${idx}-${alerta.data}` }))).slice(0, 8).map((alerta) => (
                  <div key={alerta.id} className="rounded-lg border border-white/10 bg-white/5 p-3">
                    <div className="text-sm font-medium text-white">{alerta.titulo}</div>
                    <div className="mt-1 text-xs text-white/50">{(alerta.data || '').replace('T', ' ')}</div>
                    <div className="mt-2 text-sm text-white/70">{alerta.mensagem || alerta.tipo}</div>
                  </div>
                ))}
                {!corpusDocs.some((item) => (item.alertas_disparados || []).length) && (
                  <div className="text-sm text-white/50">Sem alertas registrados.</div>
                )}
              </div>
            </GlassCard>
          </div>
        )}

        {activeTab === 'emails' && (
          <GlassCard className="overflow-hidden">
            <div className="grid grid-cols-12 border-b border-white/10 px-4 py-3 text-xs text-white/40">
              <div className="col-span-3">Assunto</div>
              <div className="col-span-2">Destino</div>
              <div className="col-span-2">Tipo</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2">Data</div>
              <div className="col-span-1 text-right">Ação</div>
            </div>
            {emails.map((item) => (
              <div key={item.id} className="grid grid-cols-12 items-center border-b border-white/5 px-4 py-3 text-sm">
                <div className="col-span-3">
                  <div className="font-medium text-white">{item.assunto}</div>
                  <div className="truncate text-xs text-white/40">{item.erro || item.corpo}</div>
                </div>
                <div className="col-span-2 text-white/60">{item.destinatario || '-'}</div>
                <div className="col-span-2 text-white/60">{item.tipo}</div>
                <div className="col-span-2"><StatusBadge value={item.status} /></div>
                <div className="col-span-2 text-white/60">{fmtDate((item.sent_at || item.created_at || '').slice(0, 10))}</div>
                <div className="col-span-1 text-right">
                  <button
                    title="Reenviar"
                    onClick={() => reenviarEmail(item.id)}
                    className="rounded-lg p-2 text-white/50 hover:bg-white/10 hover:text-white"
                    disabled={saving}
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
            {!emails.length && <div className="p-6 text-sm text-white/50">Nenhum e-mail jurídico registrado.</div>}
          </GlassCard>
        )}

        {activeTab === 'config' && (
          <div className="grid gap-4 xl:grid-cols-[1fr_360px]">
            <GlassCard className="p-5">
              <div className="mb-4 flex items-center gap-3">
                <Settings className="h-5 w-5 text-white/50" />
                <h2 className="text-lg font-semibold text-white">Escritório e SMTP</h2>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Nome do escritório"><GlassInput value={configForm.escritorio_nome || ''} onChange={(e) => setConfigForm({ ...configForm, escritorio_nome: e.target.value })} /></Field>
                <Field label="E-mail do escritório"><GlassInput type="email" value={configForm.escritorio_email || ''} onChange={(e) => setConfigForm({ ...configForm, escritorio_email: e.target.value })} /></Field>
                <Field label="SMTP servidor"><GlassInput value={configForm.smtp_server || ''} onChange={(e) => setConfigForm({ ...configForm, smtp_server: e.target.value })} /></Field>
                <Field label="SMTP porta"><GlassInput type="number" value={configForm.smtp_port || 587} onChange={(e) => setConfigForm({ ...configForm, smtp_port: e.target.value })} /></Field>
                <Field label="Autenticação"><select className="w-full rounded-lg border border-white/10 bg-slate-900/80 px-3 py-2 text-white" value={configForm.smtp_auth_mode || 'password'} onChange={(e) => setConfigForm({ ...configForm, smtp_auth_mode: e.target.value })}><option value="password">Senha SMTP/app password</option><option value="gmail_oauth">Gmail OAuth</option></select></Field>
                <Field label="TLS"><select className="w-full rounded-lg border border-white/10 bg-slate-900/80 px-3 py-2 text-white" value={configForm.smtp_use_tls ? 'sim' : 'nao'} onChange={(e) => setConfigForm({ ...configForm, smtp_use_tls: e.target.value === 'sim' })}><option value="sim">Ativo</option><option value="nao">Inativo</option></select></Field>
                <Field label="Usuário SMTP"><GlassInput value={configForm.smtp_username || ''} onChange={(e) => setConfigForm({ ...configForm, smtp_username: e.target.value })} /></Field>
                <Field label="Senha SMTP"><GlassInput type="password" value={configForm.smtp_password || ''} onChange={(e) => setConfigForm({ ...configForm, smtp_password: e.target.value })} placeholder={juridicoConfig.smtp_password ? 'mantida' : ''} /></Field>
                {configForm.smtp_auth_mode === 'gmail_oauth' && (
                  <>
                    <Field label="OAuth Client ID"><GlassInput value={configForm.oauth_client_id || ''} onChange={(e) => setConfigForm({ ...configForm, oauth_client_id: e.target.value })} /></Field>
                    <Field label="OAuth Client Secret"><GlassInput type="password" value={configForm.oauth_client_secret || ''} onChange={(e) => setConfigForm({ ...configForm, oauth_client_secret: e.target.value })} placeholder={juridicoConfig.oauth_client_secret ? 'mantido' : ''} /></Field>
                    <label className="space-y-1 md:col-span-2"><span className="text-xs text-white/50">OAuth Refresh Token</span><textarea className="min-h-20 w-full rounded-lg border border-white/10 bg-slate-900/80 px-3 py-2 text-white" value={configForm.oauth_refresh_token || ''} onChange={(e) => setConfigForm({ ...configForm, oauth_refresh_token: e.target.value })} placeholder={juridicoConfig.oauth_refresh_token ? 'mantido' : ''} /></label>
                  </>
                )}
                <Field label="Remetente padrão"><GlassInput value={configForm.mail_default_sender || ''} onChange={(e) => setConfigForm({ ...configForm, mail_default_sender: e.target.value })} /></Field>
                <label className="space-y-1 md:col-span-2"><span className="text-xs text-white/50">Observação</span><textarea className="min-h-20 w-full rounded-lg border border-white/10 bg-slate-900/80 px-3 py-2 text-white" value={configForm.observacao || ''} onChange={(e) => setConfigForm({ ...configForm, observacao: e.target.value })} /></label>
              </div>
              <div className="mt-4 flex flex-wrap justify-end gap-2">
                <GlassButton onClick={testarEmail} disabled={saving}><Mail className="h-4 w-4" /> Testar e-mail</GlassButton>
                <GlassButton onClick={salvarConfig} disabled={saving}><Settings className="h-4 w-4" /> Salvar configuração</GlassButton>
              </div>
            </GlassCard>
            <GlassCard className="p-5">
              <h3 className="text-sm font-semibold text-white">Status</h3>
              <div className="mt-4 space-y-3 text-sm text-white/70">
                <div><span className="text-white/40">Escritório:</span> {juridicoConfig.escritorio_nome || '-'}</div>
                <div><span className="text-white/40">Destino:</span> {juridicoConfig.escritorio_email || '-'}</div>
                <div><span className="text-white/40">SMTP:</span> {juridicoConfig.smtp_configurado ? 'configurado' : 'não configurado'}</div>
                <div><span className="text-white/40">Fila:</span> {emails.filter((item) => item.status === 'fila' || item.status === 'erro').length} pendente(s)</div>
              </div>
            </GlassCard>
          </div>
        )}

        {activeTab === 'dossie' && (
          <div className="grid gap-4 xl:grid-cols-[360px_1fr]">
            <GlassCard className="p-4">
              <h2 className="text-lg font-semibold text-white">Dossiê</h2>
              <select
                className="mt-3 w-full rounded-lg border border-white/10 bg-slate-900/80 px-3 py-2 text-sm text-white"
                value={selectedProcessoId}
                onChange={(e) => setSelectedProcessoId(e.target.value)}
              >
                <option value="">Selecione</option>
                {processos.map((item) => <option key={item.id} value={item.id}>{item.titulo}</option>)}
              </select>
              {selectedProcesso && (
                <div className="mt-4 space-y-3 text-sm text-white/70">
                  <div><span className="text-white/40">Status:</span> <StatusBadge value={selectedProcesso.status} /></div>
                  <div><span className="text-white/40">Cliente:</span> {selectedProcesso.cliente_nome || '-'}</div>
                  <div><span className="text-white/40">Advogado:</span> {selectedProcesso.advogado_responsavel || '-'}</div>
                  <div><span className="text-white/40">Próxima ação:</span> {selectedProcesso.proxima_acao || '-'}</div>
                  <div><span className="text-white/40">Resumo:</span> {selectedProcesso.resumo || '-'}</div>
                </div>
              )}
              <GlassButton
                className="mt-4 w-full"
                disabled={!selectedProcessoId}
                onClick={() => { setDocForm({ ...emptyDoc, processo_id: selectedProcessoId }); setModal('documento'); }}
              >
                <Upload className="h-4 w-4" /> Anexar documento
              </GlassButton>
            </GlassCard>

            <GlassCard className="p-4">
              <h2 className="mb-4 text-lg font-semibold text-white">Documentos e movimentações</h2>
              <div className="space-y-2">
                {(dossie?.documentos || []).map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 p-3">
	                    <div>
	                      <div className="text-sm font-medium text-white">{doc.nome_original}</div>
	                      <div className="text-xs text-white/50">{doc.tipo} · {fmtDate(doc.data_documento)}</div>
	                      {doc.nome_armazenado && <div className="mt-1 text-xs text-white/35">{doc.nome_armazenado}</div>}
	                    </div>
                    <div className="flex items-center gap-1">
                      <button title="Abrir" onClick={() => previewDoc(doc.id)} className="rounded-lg p-2 text-white/60 hover:bg-white/10 hover:text-white">
                        <Eye className="h-4 w-4" />
                      </button>
                      <button title="Baixar" onClick={() => downloadDoc(doc.id)} className="rounded-lg p-2 text-white/60 hover:bg-white/10 hover:text-white">
                        <Download className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
                {(dossie?.prazos || []).map((prazo) => (
                  <div key={prazo.id} className="rounded-lg border border-white/10 bg-white/5 p-3">
                    <div className="text-sm font-medium text-white">{prazo.titulo}</div>
                    <div className="text-xs text-white/50">{fmtDate(prazo.data_prazo)} · {prazo.status}</div>
                  </div>
                ))}
                {!dossie && <div className="text-sm text-white/50">Selecione um processo para ver o dossiê.</div>}
              </div>
            </GlassCard>
          </div>
        )}

        {modal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <GlassCard className="max-h-[90vh] w-full max-w-3xl overflow-y-auto p-5">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-xl font-semibold text-white">
                  {modal === 'processo' && (processoForm.id ? 'Editar processo' : 'Novo processo')}
                  {modal === 'cobranca' && (cobrancaForm.id ? 'Editar cobrança' : 'Nova cobrança')}
                  {modal === 'prazo' && 'Novo prazo'}
                  {modal === 'documento' && 'Anexar documento'}
                  {modal === 'importPdf' && 'Importar PDF jurídico'}
                </h2>
                <button onClick={closeModal} className="rounded-lg p-2 text-white/50 hover:bg-white/10 hover:text-white"><X className="h-5 w-5" /></button>
              </div>

              {modal === 'processo' && (
                <div className="grid gap-3 md:grid-cols-2">
                  <Field label="Título"><GlassInput value={processoForm.titulo} onChange={(e) => setProcessoForm({ ...processoForm, titulo: e.target.value })} /></Field>
                  <Field label="Número"><GlassInput value={processoForm.numero} onChange={(e) => setProcessoForm({ ...processoForm, numero: e.target.value })} /></Field>
                  <Field label="Cliente/Parte"><GlassInput value={processoForm.cliente_nome} onChange={(e) => setProcessoForm({ ...processoForm, cliente_nome: e.target.value })} /></Field>
                  <Field label="Advogado"><GlassInput value={processoForm.advogado_responsavel} onChange={(e) => setProcessoForm({ ...processoForm, advogado_responsavel: e.target.value })} /></Field>
                  <Field label="Status"><select className="w-full rounded-lg border border-white/10 bg-slate-900/80 px-3 py-2 text-white" value={processoForm.status} onChange={(e) => setProcessoForm({ ...processoForm, status: e.target.value })}><option value="ativo">Ativo</option><option value="risco">Risco</option><option value="suspenso">Suspenso</option><option value="encerrado">Encerrado</option></select></Field>
                  <Field label="Área"><select className="w-full rounded-lg border border-white/10 bg-slate-900/80 px-3 py-2 text-white" value={processoForm.area} onChange={(e) => setProcessoForm({ ...processoForm, area: e.target.value })}><option value="civel">Cível</option><option value="trabalhista">Trabalhista</option><option value="tributario">Tributário</option><option value="imobiliario">Imobiliário</option><option value="consumidor">Consumidor</option></select></Field>
                  <Field label="Valor da causa"><GlassInput type="number" value={processoForm.valor_causa} onChange={(e) => setProcessoForm({ ...processoForm, valor_causa: e.target.value })} /></Field>
                  <Field label="Provisão"><GlassInput type="number" value={processoForm.provisao} onChange={(e) => setProcessoForm({ ...processoForm, provisao: e.target.value })} /></Field>
                  <Field label="Próximo prazo"><GlassInput type="date" value={processoForm.proximo_prazo} onChange={(e) => setProcessoForm({ ...processoForm, proximo_prazo: e.target.value })} /></Field>
                  <Field label="Próxima ação"><GlassInput value={processoForm.proxima_acao} onChange={(e) => setProcessoForm({ ...processoForm, proxima_acao: e.target.value })} /></Field>
                  <label className="space-y-1 md:col-span-2"><span className="text-xs text-white/50">Resumo</span><textarea className="min-h-24 w-full rounded-lg border border-white/10 bg-slate-900/80 px-3 py-2 text-white" value={processoForm.resumo} onChange={(e) => setProcessoForm({ ...processoForm, resumo: e.target.value })} /></label>
                  <label className="space-y-1 md:col-span-2"><span className="text-xs text-white/50">Observação</span><textarea className="min-h-20 w-full rounded-lg border border-white/10 bg-slate-900/80 px-3 py-2 text-white" value={processoForm.observacao || ''} onChange={(e) => setProcessoForm({ ...processoForm, observacao: e.target.value })} /></label>
                  <div className="md:col-span-2 flex justify-end"><GlassButton onClick={saveProcesso} disabled={saving}><Plus className="h-4 w-4" /> Salvar</GlassButton></div>
                </div>
              )}

              {modal === 'cobranca' && (
                <div className="grid gap-3 md:grid-cols-2">
                  <Field label="Cliente"><GlassInput value={cobrancaForm.cliente_nome} onChange={(e) => setCobrancaForm({ ...cobrancaForm, cliente_nome: e.target.value })} /></Field>
                  <Field label="CPF/CNPJ"><GlassInput value={cobrancaForm.documento_cliente} onChange={(e) => setCobrancaForm({ ...cobrancaForm, documento_cliente: e.target.value })} /></Field>
                  <Field label="Unidade"><GlassInput value={cobrancaForm.unidade} onChange={(e) => setCobrancaForm({ ...cobrancaForm, unidade: e.target.value })} /></Field>
                  <Field label="Contrato"><GlassInput value={cobrancaForm.contrato} onChange={(e) => setCobrancaForm({ ...cobrancaForm, contrato: e.target.value })} /></Field>
                  <Field label="Valor original"><GlassInput type="number" value={cobrancaForm.valor_original} onChange={(e) => setCobrancaForm({ ...cobrancaForm, valor_original: e.target.value })} /></Field>
                  <Field label="Valor atualizado"><GlassInput type="number" value={cobrancaForm.valor_atualizado} onChange={(e) => setCobrancaForm({ ...cobrancaForm, valor_atualizado: e.target.value })} /></Field>
                  <Field label="Status"><select className="w-full rounded-lg border border-white/10 bg-slate-900/80 px-3 py-2 text-white" value={cobrancaForm.status} onChange={(e) => setCobrancaForm({ ...cobrancaForm, status: e.target.value })}><option value="aberta">Aberta</option><option value="enviada">Enviada</option><option value="negociacao">Negociação</option><option value="judicializada">Judicializada</option><option value="encerrada">Encerrada</option></select></Field>
                  <Field label="Próximo prazo"><GlassInput type="date" value={cobrancaForm.proximo_prazo} onChange={(e) => setCobrancaForm({ ...cobrancaForm, proximo_prazo: e.target.value })} /></Field>
                  <Field label="Próxima ação"><GlassInput value={cobrancaForm.proxima_acao} onChange={(e) => setCobrancaForm({ ...cobrancaForm, proxima_acao: e.target.value })} /></Field>
                  <Field label="Responsável"><GlassInput value={cobrancaForm.responsavel} onChange={(e) => setCobrancaForm({ ...cobrancaForm, responsavel: e.target.value })} /></Field>
                  <label className="space-y-1 md:col-span-2"><span className="text-xs text-white/50">Observação</span><textarea className="min-h-20 w-full rounded-lg border border-white/10 bg-slate-900/80 px-3 py-2 text-white" value={cobrancaForm.observacao || ''} onChange={(e) => setCobrancaForm({ ...cobrancaForm, observacao: e.target.value })} /></label>
                  <div className="md:col-span-2 flex justify-end"><GlassButton onClick={saveCobranca} disabled={saving}><Plus className="h-4 w-4" /> Salvar</GlassButton></div>
                </div>
              )}

              {modal === 'prazo' && (
                <div className="grid gap-3 md:grid-cols-2">
                  <Field label="Processo"><select className="w-full rounded-lg border border-white/10 bg-slate-900/80 px-3 py-2 text-white" value={prazoForm.processo_id} onChange={(e) => setPrazoForm({ ...prazoForm, processo_id: e.target.value })}><option value="">Sem processo</option>{processos.map((item) => <option key={item.id} value={item.id}>{item.titulo}</option>)}</select></Field>
                  <Field label="Tipo"><select className="w-full rounded-lg border border-white/10 bg-slate-900/80 px-3 py-2 text-white" value={prazoForm.tipo} onChange={(e) => setPrazoForm({ ...prazoForm, tipo: e.target.value })}><option value="prazo">Prazo</option><option value="audiencia">Audiência</option><option value="retorno">Retorno</option><option value="protocolo">Protocolo</option><option value="pagamento">Pagamento</option></select></Field>
                  <Field label="Título"><GlassInput value={prazoForm.titulo} onChange={(e) => setPrazoForm({ ...prazoForm, titulo: e.target.value })} /></Field>
                  <Field label="Data"><GlassInput type="date" value={prazoForm.data_prazo} onChange={(e) => setPrazoForm({ ...prazoForm, data_prazo: e.target.value })} /></Field>
                  <Field label="Hora"><GlassInput value={prazoForm.hora} onChange={(e) => setPrazoForm({ ...prazoForm, hora: e.target.value })} /></Field>
                  <Field label="Responsável"><GlassInput value={prazoForm.responsavel} onChange={(e) => setPrazoForm({ ...prazoForm, responsavel: e.target.value })} /></Field>
                  <div className="md:col-span-2 flex justify-end"><GlassButton onClick={savePrazo} disabled={saving}><Plus className="h-4 w-4" /> Salvar</GlassButton></div>
                </div>
              )}

              {modal === 'documento' && (
                <div className="grid gap-3 md:grid-cols-2">
                  <Field label="Processo"><select className="w-full rounded-lg border border-white/10 bg-slate-900/80 px-3 py-2 text-white" value={docForm.processo_id} onChange={(e) => setDocForm({ ...docForm, processo_id: e.target.value, cobranca_id: '' })}><option value="">Selecione</option>{processos.map((item) => <option key={item.id} value={item.id}>{item.titulo}</option>)}</select></Field>
                  <Field label="Tipo"><select className="w-full rounded-lg border border-white/10 bg-slate-900/80 px-3 py-2 text-white" value={docForm.tipo} onChange={(e) => setDocForm({ ...docForm, tipo: e.target.value })}><option value="documento">Documento</option><option value="contrato">Contrato</option><option value="notificacao">Notificação</option><option value="peticao">Petição</option><option value="decisao">Decisão</option><option value="comprovante">Comprovante</option><option value="acordo">Acordo</option></select></Field>
                  <Field label="Data"><GlassInput type="date" value={docForm.data_documento} onChange={(e) => setDocForm({ ...docForm, data_documento: e.target.value })} /></Field>
                  <Field label="Arquivo"><input className="block w-full text-sm text-white/70 file:mr-3 file:rounded-lg file:border-0 file:bg-white/10 file:px-3 file:py-2 file:text-white" type="file" onChange={(e) => setDocForm({ ...docForm, file: e.target.files?.[0] || null })} /></Field>
                  <label className="space-y-1 md:col-span-2"><span className="text-xs text-white/50">Observação</span><textarea className="min-h-20 w-full rounded-lg border border-white/10 bg-slate-900/80 px-3 py-2 text-white" value={docForm.observacao} onChange={(e) => setDocForm({ ...docForm, observacao: e.target.value })} /></label>
                  <div className="md:col-span-2 flex justify-end"><GlassButton onClick={uploadDocumento} disabled={saving}><Upload className="h-4 w-4" /> Anexar</GlassButton></div>
                </div>
              )}

              {modal === 'importPdf' && (
                <div className="grid gap-3 md:grid-cols-2">
                  <Field label="Destino"><select className="w-full rounded-lg border border-white/10 bg-slate-900/80 px-3 py-2 text-white" value={importPdfForm.destino} onChange={(e) => setImportPdfForm({ ...importPdfForm, destino: e.target.value, processo_id: '', cobranca_id: '' })}><option value="processo">Criar processo</option><option value="cobranca">Criar cobrança</option></select></Field>
                  <Field label="Origem"><select className="w-full rounded-lg border border-white/10 bg-slate-900/80 px-3 py-2 text-white" value={importPdfForm.modulo_origem} onChange={(e) => setImportPdfForm({ ...importPdfForm, modulo_origem: e.target.value })}><option value="juridico">Jurídico</option><option value="financeiro">Financeiro</option><option value="construtora">Construtora</option><option value="rh">RH</option><option value="frota">Frota</option></select></Field>
                  <Field label="Anexar a processo existente"><select className="w-full rounded-lg border border-white/10 bg-slate-900/80 px-3 py-2 text-white" value={importPdfForm.processo_id} onChange={(e) => setImportPdfForm({ ...importPdfForm, processo_id: e.target.value, cobranca_id: '' })}><option value="">Criar novo</option>{processos.map((item) => <option key={item.id} value={item.id}>{item.titulo}</option>)}</select></Field>
                  <Field label="Arquivo PDF"><input className="block w-full text-sm text-white/70 file:mr-3 file:rounded-lg file:border-0 file:bg-white/10 file:px-3 file:py-2 file:text-white" type="file" accept="application/pdf" onChange={(e) => setImportPdfForm({ ...importPdfForm, file: e.target.files?.[0] || null })} /></Field>
                  <label className="space-y-1 md:col-span-2"><span className="text-xs text-white/50">Observação</span><textarea className="min-h-20 w-full rounded-lg border border-white/10 bg-slate-900/80 px-3 py-2 text-white" value={importPdfForm.observacao} onChange={(e) => setImportPdfForm({ ...importPdfForm, observacao: e.target.value })} /></label>
                  <div className="md:col-span-2 flex justify-end"><GlassButton onClick={importarPdf} disabled={saving}><Upload className="h-4 w-4" /> Importar</GlassButton></div>
                </div>
              )}
            </GlassCard>
          </div>
        )}
      </div>
    </FadeIn>
  );
};

export default Juridico;
